import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Check } from "lucide-react";
import Seo from "../components/Seo";
import SectionEyebrow from "../components/SectionEyebrow";
import RevealOnScroll from "../components/RevealOnScroll";
import Button from "../components/Button";
import WistiaPlayer from "../components/WistiaPlayer";
import CaseStudyHero from "../components/CaseStudyHero";
import { WORKS } from "../data/content";
import { breadcrumbSchema } from "../lib/schemas";

const SchedaRow = ({ label, children }) => (
  <div className="grid grid-cols-12 gap-3 py-3 border-b border-current/15 text-[14px] leading-[1.55]">
    <dt className="col-span-5 md:col-span-4 font-condensed uppercase tracking-widest text-[12px] md:text-[13px] opacity-70">
      {label}
    </dt>
    <dd className="col-span-7 md:col-span-8">{children}</dd>
  </div>
);

const VideoCard = ({ title, caption, mediaId }) => (
  <article className="flex flex-col" data-testid={`video-${mediaId}`}>
    <span className="font-condensed uppercase tracking-widest text-[14px] text-lime mb-3">
      {title}
    </span>
    <WistiaPlayer mediaId={mediaId} />
    <p className="mt-4 text-[14px] leading-[1.55] opacity-80">{caption}</p>
  </article>
);

const KpiBlock = ({ value, label }) => (
  <div className="border-t border-dodgers/20 pt-6">
    <div
      className="font-display leading-[0.92] tracking-tight text-dodgers whitespace-nowrap"
      style={{ fontSize: "clamp(1.75rem, 3vw, 2.5rem)" }}
    >
      {value}
    </div>
    <div className="font-condensed uppercase tracking-widest text-[12px] md:text-[13px] mt-4 opacity-80 max-w-[20ch] leading-[1.35]">
      {label}
    </div>
  </div>
);

const QualBlock = ({ title, body }) => (
  <div>
    <h4 className="font-condensed uppercase tracking-widest text-sm md:text-[15px] mb-3 text-dodgers">
      {title}
    </h4>
    <p className="text-[15px] md:text-[1rem] leading-relaxed opacity-90 max-w-[40ch]">
      {body}
    </p>
  </div>
);

const ScuolaAttiva = () => {
  // Project navigation (prev / next from WORKS list — case study is the first item)
  const idx = WORKS.findIndex((w) => w.to === "/works/scuola-attiva");
  const prev = idx > 0 ? WORKS[idx - 1] : WORKS[WORKS.length - 1];
  const next = WORKS[(idx + 1) % WORKS.length];

  const breadcrumbJsonLd = breadcrumbSchema([
    { name: "Home", url: "/" },
    { name: "Works", url: "/works" },
    { name: "Scuola Attiva", url: "/works/scuola-attiva" },
  ]);

  return (
    <>
      <Seo
        title="Scuola Attiva: 1,5M studenti in 11.000 scuole | OnSports Case"
        description="Programma di educazione motoria nelle scuole italiane: 11.000+ scuole coinvolte, 1.500.000+ studenti raggiunti. Sponsorship & Impact Programs OnSports."
        path="/works/scuola-attiva"
        image="https://fast.wistia.com/embed/medias/mpd055a0n6/swatch"
        jsonLd={breadcrumbJsonLd}
      />

      {/* SEZ. 1 — HERO */}
      <CaseStudyHero
        imagePath="/images/works/scuola-attiva/hero.gif"
        imageAlt="Scuola Attiva — Sport e Salute × MIM"
        eyebrow="● Case study · Social Impact · Content & Seeding"
        titleLines={["Un manifesto.", "Un Paese in MOVIMENTO."]}
        highlightWord="MOVIMENTO."
        subtitle="Abbiamo trasformato il lancio di Scuola Attiva — il più grande progetto di sport scolastico italiano — in un'attivazione video e social capace di parlare a scuole, famiglie e istituzioni con un'unica voce."
        meta="2025/2026  ·  Cliente — Sport e Salute × MIM  ·  Categoria — Content & Seeding"
        overlayOpacity={0.55}
      />

      {/* SEZ. 2 — OVERVIEW */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-overview">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12">
            <div className="col-span-12 md:col-span-7">
              <RevealOnScroll>
                <SectionEyebrow>Overview</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">Il progetto.</h2>
              </RevealOnScroll>
              <div className="mt-8 space-y-7 text-[1.0625rem] md:text-[1.125rem] leading-relaxed max-w-[760px]">
                <RevealOnScroll delay={0.15}>
                  <p>
                    Scuola Attiva è il programma nazionale promosso da Sport e Salute e
                    dal Ministero dell'Istruzione e del Merito — in collaborazione con il
                    Ministro per lo Sport e i Giovani — per portare l'educazione motoria
                    nelle scuole italiane. Un percorso che parte dalla scuola
                    dell'infanzia, attraversa la primaria con un focus sugli schemi
                    motori di base, e si consolida nella secondaria di primo grado con
                    l'orientamento sportivo.
                  </p>
                </RevealOnScroll>
                <RevealOnScroll delay={0.2}>
                  <p>
                    Nelle sue declinazioni Kids (primaria), Junior (secondaria di I
                    grado) e Infanzia, il progetto coinvolge ogni anno oltre 11.000
                    scuole, più di 1,5 milioni di studenti e migliaia di tutor sportivi
                    laureati in Scienze Motorie. Realizzato in collaborazione con le
                    Federazioni Sportive Nazionali e il Comitato Italiano Paralimpico,
                    allineato agli obiettivi dell'OMS e all'Agenda 2030 dell'ONU.
                  </p>
                </RevealOnScroll>
              </div>
            </div>
            <div className="col-span-12 md:col-span-5">
              <RevealOnScroll delay={0.25}>
                <div className="border border-dodgers/20 p-6 md:p-7" data-testid="scheda-progetto">
                  <span className="font-condensed uppercase tracking-widest text-xs opacity-60 block mb-5">
                    Scheda progetto
                  </span>
                  <dl>
                    <SchedaRow label="Progetto">Scuola Attiva (Kids · Junior · Infanzia)</SchedaRow>
                    <SchedaRow label="Cliente">Sport e Salute S.p.A.</SchedaRow>
                    <SchedaRow label="Partner">
                      MIM · Dipartimento per lo Sport<br />
                      FSN · CIP
                    </SchedaRow>
                    <SchedaRow label="Pilastro">Social Impact · Content &amp; IP Studio</SchedaRow>
                    <SchedaRow label="Ruolo">
                      Contenuti video del manifesto di lancio<br />
                      Seeding social
                    </SchedaRow>
                    <SchedaRow label="Anno">2025/2026</SchedaRow>
                  </dl>
                </div>
              </RevealOnScroll>
            </div>
          </div>
        </div>
      </section>

      {/* SEZ. 3 — LA SFIDA */}
      <section className="section-pad bg-dodgers text-galactico" data-testid="section-sfida">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12">
            <div className="col-span-12 md:col-span-5">
              <RevealOnScroll>
                <SectionEyebrow>La sfida</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">
                  Comunicare un progetto pubblico, parlando a tutti.
                </h2>
              </RevealOnScroll>
            </div>
            <div className="col-span-12 md:col-span-7 space-y-7 text-[1.0625rem] md:text-[1.125rem] leading-relaxed max-w-[64ch] opacity-90">
              <RevealOnScroll delay={0.15}>
                <p>
                  I progetti istituzionali di scala nazionale condividono un problema
                  strutturale di comunicazione: parlano simultaneamente a pubblici molto
                  diversi — dirigenti scolastici, insegnanti, tutor, famiglie, ragazzi,
                  media, decisori politici — e rischiano di non parlare davvero a
                  nessuno.
                </p>
              </RevealOnScroll>
              <RevealOnScroll delay={0.22}>
                <p>
                  Il lancio di Scuola Attiva richiedeva qualcosa di più di un comunicato
                  o di una brochure ministeriale. Serviva un manifesto: un dispositivo
                  narrativo capace di tradurre un programma articolato e tecnico in un
                  messaggio chiaro, emotivamente memorabile, replicabile sui social
                  media dove insegnanti e famiglie passano realmente le loro giornate.
                  La sfida che abbiamo accettato: dare a Scuola Attiva una voce
                  contemporanea, calda e attivante, senza tradire la natura
                  istituzionale e l'autorevolezza del progetto.
                </p>
              </RevealOnScroll>
            </div>
          </div>
        </div>
      </section>

      {/* SEZ. 4 — IL MANIFESTO (VIDEO WISTIA) */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-manifesto">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-12 md:mb-14">
            <div className="col-span-12 md:col-span-7">
              <RevealOnScroll>
                <SectionEyebrow>Il manifesto</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">Tre voci. Un solo messaggio.</h2>
              </RevealOnScroll>
              <RevealOnScroll delay={0.18}>
                <p className="mt-7 text-[1.0625rem] md:text-[1.125rem] leading-relaxed max-w-[60ch]">
                  Abbiamo prodotto tre video manifesto pensati per parlare alle tre
                  anime del progetto: le scuole, i tutor sportivi e le famiglie. Stesso
                  linguaggio visivo, tre angoli narrativi diversi — ognuno costruito per
                  attivare un comportamento specifico nel proprio pubblico.
                </p>
              </RevealOnScroll>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <RevealOnScroll delay={0.05}>
              <VideoCard
                title="Manifesto principale"
                caption="Il film di lancio del progetto."
                mediaId="mpd055a0n6"
              />
            </RevealOnScroll>
            <RevealOnScroll delay={0.12}>
              <VideoCard
                title="Declinazione 02"
                caption="Il taglio dedicato ai tutor sportivi e all'attivazione sul territorio."
                mediaId="06c0b0vzne"
              />
            </RevealOnScroll>
            <RevealOnScroll delay={0.19}>
              <VideoCard
                title="Declinazione 03"
                caption="Il taglio dedicato a famiglie e ragazzi, per il consumo nel feed mobile."
                mediaId="ruyyv8cr80"
              />
            </RevealOnScroll>
          </div>
        </div>
      </section>

      {/* SEZ. 5 — APPROCCIO */}
      <section className="section-pad bg-dodgers text-galactico" data-testid="section-approccio">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-12">
            <div className="col-span-12 md:col-span-7">
              <RevealOnScroll>
                <SectionEyebrow>Approccio</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">Dal manifesto al feed.</h2>
              </RevealOnScroll>
              <RevealOnScroll delay={0.18}>
                <p className="mt-7 text-[1.0625rem] md:text-[1.125rem] leading-relaxed max-w-[60ch] opacity-90">
                  Abbiamo costruito l'attivazione di lancio attorno a due assi
                  integrati: la produzione dei contenuti video del manifesto e il
                  seeding social della campagna. Non due deliverable separati, ma un
                  unico sistema progettato perché ogni asset video fosse pensato fin
                  dall'origine per il comportamento dei social — formati verticali e
                  orizzontali, durate ottimizzate, hook costruiti per trattenere lo
                  scroll, sottotitoli always-on per il consumo silenzioso del feed
                  mobile.
                </p>
              </RevealOnScroll>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-0 md:divide-x md:divide-lime">
            <RevealOnScroll delay={0.1}>
              <div className="md:pr-12">
                <span
                  className="font-display text-lime block leading-none mb-4"
                  style={{ fontSize: "clamp(2.5rem, 4vw, 3.5rem)" }}
                >
                  01
                </span>
                <h3 className="font-condensed uppercase tracking-widest text-sm mb-5 opacity-85">
                  Contenuti video
                </h3>
                <p className="text-[15px] md:text-[1rem] leading-relaxed opacity-90 mb-7 max-w-[44ch]">
                  Direzione creativa e produzione dei video di lancio: dal concept al
                  cut finale.
                </p>
                <ul className="space-y-3">
                  {[
                    "Concept del manifesto e tone narrativo coerente con il posizionamento di Sport e Salute e MIM",
                    "Storyboard, casting e selezione delle location",
                    "Produzione integrale: riprese video, fotografia di scena, direzione artistica",
                    "Montaggio e post-produzione del manifesto principale + 2 declinazioni",
                    "Versioning multi-formato (16:9 / 9:16 / 1:1) e multi-durata (60\" / 30\" / 15\")",
                    "Sound design e music selection",
                    "Sottotitoli always-on per accessibilità",
                  ].map((it, i) => (
                    <li key={i} className="flex items-start gap-3 text-[15px] leading-relaxed">
                      <span className="mt-1 flex-shrink-0 w-5 h-5 bg-lime text-dodgers flex items-center justify-center">
                        <Check size={12} strokeWidth={3} />
                      </span>
                      <span className="opacity-90">{it}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </RevealOnScroll>

            <RevealOnScroll delay={0.18}>
              <div className="md:pl-12">
                <span
                  className="font-display text-lime block leading-none mb-4"
                  style={{ fontSize: "clamp(2.5rem, 4vw, 3.5rem)" }}
                >
                  02
                </span>
                <h3 className="font-condensed uppercase tracking-widest text-sm mb-5 opacity-85">
                  Seeding social
                </h3>
                <p className="text-[15px] md:text-[1rem] leading-relaxed opacity-90 mb-7 max-w-[44ch]">
                  Pianificazione e attivazione della distribuzione sui canali strategici
                  per il target del progetto.
                </p>
                <ul className="space-y-3">
                  {[
                    "Mappatura dei canali rilevanti: Sport e Salute, MIM, Federazioni Sportive Nazionali, community verticali della scuola",
                    "Calendario editoriale di lancio coordinato con la roadmap istituzionale",
                    "Adattamento dei video in caroselli, reel, post statici, stories per Instagram, Facebook, YouTube, LinkedIn",
                    "Copy social tarati per voce di ogni canale, con CTA differenziate (adesione scuole, candidature tutor, coinvolgimento famiglie)",
                    "Monitoring in real-time e ottimizzazione progressiva del seeding",
                  ].map((it, i) => (
                    <li key={i} className="flex items-start gap-3 text-[15px] leading-relaxed">
                      <span className="mt-1 flex-shrink-0 w-5 h-5 bg-lime text-dodgers flex items-center justify-center">
                        <Check size={12} strokeWidth={3} />
                      </span>
                      <span className="opacity-90">{it}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </RevealOnScroll>
          </div>
        </div>
      </section>

      {/* SEZ. 6 — IMPATTO */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-impatto">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-12 md:mb-16">
            <div className="col-span-12 md:col-span-7">
              <RevealOnScroll>
                <SectionEyebrow>Impatto</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">Misurato sul Paese reale.</h2>
              </RevealOnScroll>
              <RevealOnScroll delay={0.18}>
                <p className="mt-7 text-[1.0625rem] md:text-[1.125rem] leading-relaxed max-w-[60ch]">
                  Il valore di una campagna di lancio per un progetto istituzionale non
                  si misura solo in metriche social. Si misura nel comportamento
                  concreto del target: scuole che aderiscono, tutor che si candidano,
                  famiglie che riconoscono il progetto nelle aule dei propri figli.
                </p>
              </RevealOnScroll>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10">
            <RevealOnScroll delay={0.05}>
              <KpiBlock value="11.000+" label="Scuole coinvolte dal progetto" />
            </RevealOnScroll>
            <RevealOnScroll delay={0.1}>
              <KpiBlock value="1.500.000+" label="Studenti raggiunti annualmente" />
            </RevealOnScroll>
            <RevealOnScroll delay={0.15}>
              <KpiBlock value="2.000+" label="Tutor sportivi formati sul territorio" />
            </RevealOnScroll>
            <RevealOnScroll delay={0.2}>
              <KpiBlock value="1.500.000+" label="Visualizzazioni aggregate del manifesto" />
            </RevealOnScroll>
          </div>

          <p className="mt-8 text-[13px] leading-[1.55] text-mandrogni opacity-80 max-w-[80ch]">
            I primi tre dati si riferiscono alla scala del programma Scuola Attiva nelle
            edizioni 2024/25 e 2025/26 secondo i dati pubblicati da Sport e Salute. Il
            dato di visualizzazioni aggregate del manifesto è stimato sui canali
            istituzionali e partner coinvolti nel piano di seeding.
          </p>

          <div className="mt-16 md:mt-20 grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
            <RevealOnScroll delay={0.05}>
              <QualBlock
                title="Riconoscibilità"
                body="Il manifesto ha dato a Scuola Attiva un'identità visiva e narrativa unitaria, capace di funzionare in modo coerente sui canali ministeriali, di Sport e Salute, delle Federazioni e nelle aule."
              />
            </RevealOnScroll>
            <RevealOnScroll delay={0.12}>
              <QualBlock
                title="Attivazione"
                body="Il seeding ha portato i materiali nelle conversazioni dove il target reale del progetto si trova — community di insegnanti, gruppi di dirigenti, pagine delle famiglie — invece di limitarsi alla comunicazione top-down istituzionale."
              />
            </RevealOnScroll>
            <RevealOnScroll delay={0.19}>
              <QualBlock
                title="Replicabilità"
                body="Linee guida e asset modulari hanno reso la comunicazione di Scuola Attiva sostenibile nel tempo, anche oltre la fase di lancio."
              />
            </RevealOnScroll>
          </div>
        </div>
      </section>

      {/* SEZ. 7 — CREDITS */}
      <section className="py-20 md:py-28 bg-dodgers text-galactico" data-testid="section-credits">
        <div className="container-os">
          <RevealOnScroll>
            <SectionEyebrow>Credits</SectionEyebrow>
          </RevealOnScroll>
          <RevealOnScroll delay={0.1}>
            <div className="mt-8 max-w-[720px]">
              <dl>
                <SchedaRow label="Cliente">Sport e Salute S.p.A.</SchedaRow>
                <SchedaRow label="Partner istituzionali">
                  Ministero dell'Istruzione e del Merito<br />
                  Dipartimento per lo Sport — PCM<br />
                  Federazioni Sportive Nazionali<br />
                  Comitato Italiano Paralimpico
                </SchedaRow>
                <SchedaRow label="Ruolo OnSports">
                  Concept, produzione video, seeding social
                </SchedaRow>
                <SchedaRow label="Servizi">
                  Content &amp; IP Studio · Social Impact Activation
                </SchedaRow>
                <SchedaRow label="Anno">2025/2026</SchedaRow>
              </dl>
            </div>
          </RevealOnScroll>
        </div>
      </section>

      {/* SEZ. 8 — CTA FINALE */}
      <section className="bg-dodgers text-galactico py-24 md:py-40 border-t border-galactico/10" data-testid="section-cta-finale">
        <div className="container-os">
          <RevealOnScroll>
            <h2 className="display-h2 max-w-[24ch]">
              Hai un progetto a impatto sociale da lanciare? Costruiamo insieme la sua
              voce, la sua immagine, il suo <span className="text-lime">IMPATTO</span>.
            </h2>
          </RevealOnScroll>
          <RevealOnScroll delay={0.1}>
            <p className="mt-8 body-lg opacity-85 max-w-[60ch]">
              Raccontaci il tuo progetto. Lavoriamo con istituzioni, brand e property
              che vogliono trasformare lo sport in una leva di cambiamento reale e
              documentabile.
            </p>
          </RevealOnScroll>
          <RevealOnScroll delay={0.18}>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/contatti" data-testid="cta-parliamone">
                <Button variant="primary">Parliamone</Button>
              </Link>
              <Link to="/works" data-testid="cta-altri-casi">
                <Button variant="secondary-light">Vedi altri casi studio</Button>
              </Link>
            </div>
          </RevealOnScroll>
        </div>
      </section>

      {/* SEZ. 9 — NAVIGAZIONE PROGETTI */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-nav-progetti">
        <div className="container-os">
          <RevealOnScroll>
            <SectionEyebrow>Naviga i progetti</SectionEyebrow>
          </RevealOnScroll>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
            <Link
              to={prev.to || "/works"}
              className="group border border-dodgers/20 p-7 md:p-10 hover:border-dodgers transition-colors flex flex-col gap-4"
              data-hover="true"
              data-testid="nav-prev"
            >
              <span className="font-condensed uppercase tracking-widest text-xs opacity-70">
                ← Progetto precedente
              </span>
              <span className="font-display text-2xl md:text-3xl leading-tight group-hover:text-mandrogni transition-colors">
                {prev.title}
              </span>
              <span className="text-sm opacity-75">{prev.category}</span>
            </Link>
            <Link
              to={next.to || "/works"}
              className="group border border-dodgers/20 p-7 md:p-10 hover:border-dodgers transition-colors flex flex-col gap-4 md:text-right md:items-end"
              data-hover="true"
              data-testid="nav-next"
            >
              <span className="font-condensed uppercase tracking-widest text-xs opacity-70 inline-flex items-center gap-2">
                Progetto successivo <ArrowUpRight size={14} />
              </span>
              <span className="font-display text-2xl md:text-3xl leading-tight group-hover:text-mandrogni transition-colors">
                {next.title}
              </span>
              <span className="text-sm opacity-75">{next.category}</span>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default ScuolaAttiva;
